/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAppDispatch, useAppSelector } from 'app/state'
import { fetchProvidersMappingRequest } from 'features/data-observatory/state/dataObservatorySlice'
import { Subscription } from 'features/data-observatory/types'
import { useCallback, useEffect, useMemo } from 'react'
import { DataObservatoryTreeItem } from '../DataObservatoryPanel'

function useSubscriptions(
  { filterSamples, filterTables }: { filterSamples?: boolean; filterTables?: string[] } = { filterSamples: false }
) {
  const dispatch = useAppDispatch()
  const providers = useAppSelector((state) => state.dataObservatory.providers)
  const providersObject = useMemo(() => {
    const providersObj = {}
    providers?.forEach((p) => (providersObj[p.id] = p.name))
    return providersObj
  }, [providers])
  const samples = useAppSelector((state) => state.dataObservatory.samples)
  const subscriptions = useAppSelector((state) => state.dataObservatory.subscriptions)
  const subscriptionsStatus = useAppSelector((state) => state.dataObservatory.subscriptionsStatus)

  useEffect(() => {
    if (!providers?.length) {
      dispatch(fetchProvidersMappingRequest())
    }
  }, [dispatch, providers])

  const getProviderIdByName = useCallback(
    (name) => {
      if (!providers?.length) return
      return providers.find((prov) => prov.name === name)
    },
    [providers]
  )

  const fillSubscriptionInTree = useCallback(
    (tree: any, subscription: Subscription) => {
      const providerId = getProviderIdByName(subscription.dataset_provider)?.id
      if (!providerId) return tree

      if (tree[providerId]?.subscriptions) {
        tree[providerId].subscriptions.push(subscription)
      } else {
        tree[providerId] = { subscriptions: [subscription] }
      }
      return tree
    },
    [getProviderIdByName]
  )

  const fillSamplesInTree = useCallback(
    (tree: any, subscription: Subscription) => {
      const providerId = getProviderIdByName(subscription.dataset_provider)?.id
      if (!providerId) return tree

      if (tree[providerId]?.samples) {
        tree[providerId].samples.push(subscription)
      } else {
        tree[providerId] = { ...tree[providerId], samples: [subscription] }
      }
      return tree
    },
    [getProviderIdByName]
  )

  const buildSubscritionTree = useCallback(() => {
    let filteredSubscriptions = subscriptions
    if (filterTables) {
      filteredSubscriptions = filteredSubscriptions.filter((s) => {
        return filterTables.includes(s.dataset_table) || filterTables.includes(`sub_${s.dataset_slug}`)
      })
    }
    const tree = filteredSubscriptions.reduce((tree: any, subscription: Subscription) => {
      return fillSubscriptionInTree(tree, subscription)
    }, {})

    if (!filterSamples) {
      let filteredSamples = samples
      if (filterTables) {
        filteredSamples = filteredSamples.filter((s) => {
          return filterTables.includes(s.dataset_table) || filterTables.includes(`sample_${s.dataset_slug}`)
        })
      }
      filteredSamples.reduce((tree: any, subscription: Subscription) => {
        return fillSamplesInTree(tree, subscription)
      }, tree)
    }

    return tree
  }, [fillSubscriptionInTree, fillSamplesInTree, samples, subscriptions, filterSamples, filterTables])

  const subscriptionsTree = useMemo(buildSubscritionTree, [buildSubscritionTree])

  const subscriptionsList = useMemo(() => {
    return subscriptionsTree
      ? Object.entries(subscriptionsTree).map((item) => {
          const id = item[0]
          const children: Record<string, unknown>[] = item[1] as Record<string, unknown>[]
          const formattedChildren = Object.keys(children).map((child) => {
            return {
              id: `${id}.${child}`,
              type: 'folder',
              label: child,
              children: children[child].map((childItem) => ({
                ...childItem,
                type: child,
                label: childItem.dataset_name || childItem.dataset_slug,
                id: childItem.id
              }))
            }
          })
          return {
            id,
            label: providers?.length ? providers[id] : id,
            type: 'provider',
            children: formattedChildren
          } as DataObservatoryTreeItem
        })
      : []
  }, [subscriptionsTree, providers])

  return {
    samples,
    subscriptions,
    subscriptionsStatus,
    subscriptionsTree,
    subscriptionsList,
    providers: providersObject
  }
}

export default useSubscriptions
